import type { ComponentSize } from "apps/visits/core/types/design";
import clsx from "clsx";
import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";

interface InputTextProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  labelExtra?: string | ReactNode;
  error?: string;
  size?: ComponentSize;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
}

const InputText = forwardRef<HTMLInputElement, InputTextProps>(
  (
    { label, error, required = false, size, className, id, endAdornment, startAdornment, labelExtra, ...props },
    ref,
  ) => {
    const inputClasses = clsx(
      "input input-bordered w-full",
      endAdornment && "pr-8",
      startAdornment && "pl-8",
      error && "input-error",
      size && `input-${size}`,
    );

    return (
      <div className="form-control relative">
        {label && (
          <label className="label pt-0" htmlFor={id}>
            <span className="label-text flex items-center">
              {label}
              {required && "*"}
              {labelExtra}
            </span>
          </label>
        )}
        <div className="relative">
          {startAdornment && (
            <span className="absolute top-1/2 left-4 -translate-y-1/2 text-base-300 text-md">{startAdornment}</span>
          )}
          <input type="text" className={inputClasses} ref={ref} {...props} id={id} />
          {endAdornment && (
            <span className="absolute top-1/2 right-4 -translate-y-1/2 text-base-300 text-md">{endAdornment}</span>
          )}
        </div>
        {error && (
          <label className="label absolute -bottom-5 p-0" htmlFor={id}>
            <span className="label-text-alt text-error">{error}</span>
          </label>
        )}
      </div>
    );
  },
);

export default InputText;
